/* eslint-disable max-lines-per-function */
import isNil from 'lodash/isNil';

import { PatientSimple, PatientSimpleQuery } from '@dreryk/egabinet-patient-frontend-api';

import { CqrsQueryPerformResult, performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import { Direction, paginations } from '@dreryk/command-query-ws-client';
import { assertUnreachable } from '@dreryk/react-common';
import { Slot } from '@dreryk/watermelon';

import { GuardianDetailsFormBean } from '../GuardianDetailsModal/GuardianDetailsModal';

const pagination = paginations
   .all<PatientSimple>()
   .orderedBy('lastName', Direction.ASCENDING)
   .orderedBy('firstName', Direction.ASCENDING)
   .orderedBy('externalIdentifier', Direction.ASCENDING);

// eslint-disable-next-line max-lines-per-function
export const patientSearchCreator = (patientId: number | undefined, peselOnlySearch: boolean) => {
   // eslint-disable-next-line max-lines-per-function
   return (searchText: string | null) => {
      return async (): CqrsQueryPerformResult<GuardianDetailsFormBean[]> => {
         const patients = await performCqrsQuerySearch<PatientSimple>(() => {
            const patientSimpleQuery = new PatientSimpleQuery();
            patientSimpleQuery.freeformCriteria = Slot.emptyIfNull(searchText);
            if (patientId) {
               patientSimpleQuery.excludedId = Slot.emptyIfNull(patientId);
            }
            if (peselOnlySearch) {
               patientSimpleQuery.externalIdentifierTypes = Slot.of(['PESEL']);
            } else {
               patientSimpleQuery.externalIdentifierTypes = Slot.of(['PESEL', 'PASSPORT', 'NATIONAL']);
            }
            return patientSimpleQuery;
         }, pagination);
         // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
         return patients
            .filter(
               (patient) =>
                  !isNil(patient.firstName) &&
                  !isNil(patient.lastName) &&
                  !isNil(patient.referenceIdentifier) &&
                  !isNil(patient.address)
            )
            .map(
               (patient): GuardianDetailsFormBean => {
                  return {
                     firstName: patient.firstName!.asString(),
                     middleName: patient.middleName?.asString() ?? null,
                     lastName: patient.lastName!.asString(),
                     gender: patient.gender ?? null,
                     ...patient
                        .referenceIdentifier!.findExternalIdentifier()!
                        .accept<
                           Pick<
                              GuardianDetailsFormBean,
                              | 'guardianExternalIdentifierType'
                              | 'guardianExternalIdentifierValue'
                              | 'passportCountry'
                              | 'passportValidTo'
                              | 'nationalIdentificationNumberCountry'
                           >
                        >({
                           visitPesel: (pesel) => ({
                              guardianExternalIdentifierType: 'Pesel',
                              guardianExternalIdentifierValue: pesel.asString(),
                              nationalIdentificationNumberCountry: null,
                              passportCountry: null,
                              passportValidTo: null,
                           }),
                           visitGuardianPesel: () => assertUnreachable(undefined),
                           visitPassport: (passport) => ({
                              guardianExternalIdentifierType: 'Passport',
                              guardianExternalIdentifierValue: passport.getValue(),
                              nationalIdentificationNumberCountry: null,
                              passportCountry: passport.getCountry(),
                              passportValidTo: passport.getValidTo(),
                           }),
                           visitNationalIdentificationNumber: (nationalIdentificationNumber) => ({
                              guardianExternalIdentifierType: 'NationalIdentificationNumber',
                              guardianExternalIdentifierValue: nationalIdentificationNumber.getValue(),
                              nationalIdentificationNumberCountry: nationalIdentificationNumber.getCountry(),
                              passportCountry: null,
                              passportValidTo: null,
                           }),
                        }),
                     town: patient.address!.findTown()?.getName() ?? null,
                     country: patient.address!.getCountry(),
                     flat: patient.address!.findFlat()?.asString() ?? null,
                     house: patient.address!.findBuilding()?.asString() ?? null,
                     postcode: patient.address!.findPostcode() ?? null,
                     street: patient.address!.findStreet()?.asString() ?? null,
                     emailAddress: null,
                     phoneNumber: null,
                  };
               }
            );
      };
   };
};
